/**
 * Data Sanitization & PII Masking Utility
 * Redacts credentials, tokens, and sensitive information from audit logs and client views.
 */

const REDACTED_KEYS = [
  "password",
  "secret",
  "mfasecret",
  "mfabackupcodes",
  "accesstoken",
  "refreshtoken",
  "clientsecret",
  "apikey",
  "token",
  "pin",
  "authorization",
  "cvv",
  "ssn"
];

/**
 * Recursively sanitize an object or payload by redacting sensitive keys
 * @param {any} data
 * @returns {any}
 */
export function sanitizePayload(data) {
  if (data === null || data === undefined) return data;
  if (typeof data !== "object") return data;

  if (Array.isArray(data)) {
    return data.map((item) => sanitizePayload(item));
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase().replace(/[-_]/g, "");
    if (REDACTED_KEYS.some((k) => lowerKey.includes(k))) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizePayload(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Mask financial account or credit card numbers (e.g. •••• •••• •••• 4589)
 * @param {string} numberStr
 * @returns {string}
 */
export function maskFinancialNumber(numberStr = "") {
  if (!numberStr) return "";
  const clean = numberStr.replace(/\s+/g, "");
  if (clean.length < 4) return "••••";
  const lastFour = clean.slice(-4);
  return `•••• •••• •••• ${lastFour}`;
}

/**
 * Mask Tax ID / SSN / EIN (e.g. ••-•••1234)
 * @param {string} taxId
 * @returns {string}
 */
export function maskTaxId(taxId = "") {
  if (!taxId) return "";
  const clean = taxId.replace(/[^0-9A-Za-z]/g, "");
  if (clean.length < 4) return "••-••••••";
  const lastFour = clean.slice(-4);
  return `••-•••${lastFour}`;
}
